import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navItems = [
  { label: '홈', path: '/' },
  { label: '지부 소개', path: '/about' },
  { label: '수강생 모집', path: '/academy' },
  { label: '자격 및 인증', path: '/certification' },
  { label: '커뮤니티', path: '/community' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const isHome = location.pathname === '/';
  const isDark = isHome && !scrolled;

  return (
    <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${scrolled || !isHome ? 'bg-white border-b border-gray-200 shadow-sm' : 'bg-transparent'}`}>
      {/* Top bar - only on scroll/non-home */}
      {(scrolled || !isHome) && (
        <div className="bg-[#1B3A6B] hidden md:block">
          <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-8">
            <p className="text-white/70 text-xs">(사)대한문신사중앙회 공식 인증 중구지부</p>
            <div className="flex items-center gap-4">
              <a href="tel:010-6740-4061" className="text-white/70 text-xs flex items-center gap-1 hover:text-white cursor-pointer">
                <i className="ri-phone-line text-xs" /> 010-6740-4061
              </a>
              <span className="text-white/30 text-xs">|</span>
              <a href="mailto:a01067404061@gmail.com" className="text-white/70 text-xs flex items-center gap-1 hover:text-white cursor-pointer">
                <i className="ri-mail-line text-xs" /> a01067404061@gmail.com
              </a>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <img
            src="https://public.readdy.ai/ai/img_res/7d6ce8a8-ae30-4065-ad4c-0a166a63deb4.png"
            alt="대한문신사중앙회 중구지부 로고"
            className={`h-10 w-auto object-contain ${isDark ? '' : ''}`}
          />
          <div className="hidden sm:flex items-center gap-2.5">
            <div className={`flex flex-col items-center justify-center border rounded-sm px-2 py-1 flex-shrink-0 relative overflow-hidden ${isDark ? 'border-[#C9A84C]/70 bg-[#C9A84C]/10' : 'border-[#C9A84C] bg-gradient-to-b from-[#C9A84C]/15 to-[#C9A84C]/5'}`}>
              <span className={`text-[8.5px] font-black leading-none tracking-[0.15em] ${isDark ? 'text-[#C9A84C]' : 'text-[#C9A84C]'}`}>사단</span>
              <span className={`w-full h-px my-0.5 ${isDark ? 'bg-[#C9A84C]/40' : 'bg-[#C9A84C]/40'}`} />
              <span className={`text-[8.5px] font-black leading-none tracking-[0.15em] ${isDark ? 'text-[#C9A84C]' : 'text-[#C9A84C]'}`}>법인</span>
            </div>
            <div>
              <p className={`text-xs font-bold leading-tight tracking-wide ${isDark ? 'text-white' : 'text-[#1B3A6B]'}`}>대한문신사중앙회</p>
              <p className={`text-xs leading-tight ${isDark ? 'text-white/70' : 'text-gray-500'}`}>중구지부</p>
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`text-sm font-medium tracking-wide transition-colors whitespace-nowrap cursor-pointer relative group ${
                location.pathname === item.path
                  ? isDark ? 'text-[#C9A84C]' : 'text-[#1B3A6B] font-semibold'
                  : isDark ? 'text-white/90 hover:text-[#C9A84C]' : 'text-gray-600 hover:text-[#1B3A6B]'
              }`}
            >
              {item.label}
              {location.pathname === item.path && (
                <span className={`absolute -bottom-1 left-0 w-full h-0.5 ${isDark ? 'bg-[#C9A84C]' : 'bg-[#1B3A6B]'}`} />
              )}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/admin"
            className={`whitespace-nowrap px-4 py-2.5 rounded-md text-sm font-semibold border transition-colors cursor-pointer flex items-center gap-1.5 ${isDark ? 'border-white/30 text-white hover:bg-white/10' : 'border-[#1B3A6B]/30 text-[#1B3A6B] hover:bg-[#1B3A6B]/5'}`}
          >
            <i className="ri-admin-line text-sm" /> 관리자
          </Link>
          <Link
            to="/academy#consult"
            className="whitespace-nowrap px-5 py-2.5 rounded-md text-sm font-semibold bg-[#1B3A6B] text-white hover:bg-[#15305a] transition-colors cursor-pointer"
          >
            수강 상담 신청
          </Link>
        </div>

        <button
          className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${isDark ? 'text-white' : 'text-[#1B3A6B]'}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="메뉴 열기"
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <nav className="flex flex-col px-6 py-4 gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium py-3 border-b border-gray-50 cursor-pointer ${location.pathname === item.path ? 'text-[#1B3A6B] font-semibold' : 'text-gray-700'}`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/admin"
              className="whitespace-nowrap mt-3 px-5 py-3 rounded-md text-sm font-semibold border border-[#1B3A6B]/30 text-[#1B3A6B] text-center cursor-pointer flex items-center justify-center gap-1.5"
            >
              <i className="ri-admin-line text-sm" /> 관리자
            </Link>
            <Link
              to="/academy#consult"
              className="whitespace-nowrap mt-2 px-5 py-3 rounded-md text-sm font-semibold bg-[#1B3A6B] text-white text-center cursor-pointer"
            >
              수강 상담 신청
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
